import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertTriangle, Award, Layers3 } from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
} from "chart.js";

import {
    DashboardSection, DashboardTable, DistributionBars, SectionGrid, StatTile, TileGrid,
} from "../dashboard";
import { Button } from "../ui/button";
import StatusPill from "./StatusPill";

/* The HR block on the Appraisal dashboard: company-wide appraisal state.

   Built from the shared dashboard kit. Two of the four charts here were
   single-series categorical counts — a status doughnut and a rating bar — and
   those are now DistributionBars, which is what the kit is for. Department
   Progress keeps chart.js: it stacks three series per department, which is a
   genuine point-by-point comparison and the one case the kit's own guidance
   says to reach for a chart. */

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const STATUS_BUCKETS = [
    { key: "draft", label: "Draft", bar: "bg-slate-400" },
    { key: "pending_employee_submission", label: "Pending Employee", bar: "bg-amber-500" },
    { key: "in_progress", label: "In Progress", bar: "bg-blue-500" },
    { key: "submitted_to_rm", label: "With RM", bar: "bg-indigo-500" },
    { key: "sent_back_by_rm", label: "Sent Back (RM)", bar: "bg-orange-500" },
    { key: "rm_reviewed", label: "RM Reviewed", bar: "bg-sky-500" },
    { key: "pending_hr_approval", label: "Pending HR", bar: "bg-violet-500" },
    { key: "sent_back_by_hr", label: "Sent Back (HR)", bar: "bg-rose-500" },
    { key: "hr_approved", label: "HR Approved", bar: "bg-green-500" },
    { key: "published", label: "Published", bar: "bg-emerald-500" },
    { key: "closed", label: "Closed", bar: "bg-slate-500" },
    { key: "overdue", label: "Overdue", bar: "bg-red-500" },
];

const RATING_BANDS = [
    { label: "Outstanding", min: 4.5, bar: "bg-emerald-500" },
    { label: "Exceeds", min: 3.5, bar: "bg-sky-500" },
    { label: "Meets", min: 2.5, bar: "bg-amber-500" },
    { label: "Needs Improvement", min: 1.5, bar: "bg-orange-500" },
    { label: "Poor", min: 0, bar: "bg-red-500" },
];

const bandOf = (score) => {
    if (score === null || score === undefined) return null;
    const s = Number(score);
    for (const b of RATING_BANDS) if (s >= b.min) return b.label;
    return "Poor";
};

const PENDING_COLUMNS = [
    {
        key: "employee",
        header: "Employee",
        render: (a) => (
            <span className="block min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {a.employee?.name || "Employee"}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                    {a.employee?.designation}{a.employee?.department ? ` · ${a.employee.department}` : ""}
                </span>
            </span>
        ),
    },
    {
        key: "status",
        header: "Status",
        className: "text-right",
        headClassName: "text-right",
        render: (a) => <StatusPill value={a.status} />,
    },
];

const HRGovernanceSummary = ({ appraisals = [] }) => {
    const navigate = useNavigate();

    const counts = useMemo(() => {
        const c = Object.fromEntries(STATUS_BUCKETS.map((b) => [b.key, 0]));
        for (const a of appraisals) {
            if (c[a.status] !== undefined) c[a.status] += 1;
        }
        return c;
    }, [appraisals]);

    const total = appraisals.length;
    const pendingHR = counts.pending_hr_approval;
    const published = counts.published;
    const overdue = counts.overdue;

    const statusRows = useMemo(
        () => STATUS_BUCKETS
            .filter((b) => counts[b.key] > 0)
            .map((b) => ({ key: b.key, label: b.label, count: counts[b.key], bar: b.bar })),
        [counts],
    );

    // Department-wise completion: stacked Published vs Pending HR vs In Flight.
    const departmentStats = useMemo(() => {
        const m = new Map();
        for (const a of appraisals) {
            const dept = a.employee?.department || "Unassigned";
            if (!m.has(dept)) m.set(dept, { published: 0, in_flight: 0, pending_hr: 0 });
            const row = m.get(dept);
            if (a.status === "published") row.published += 1;
            else if (a.status === "pending_hr_approval") row.pending_hr += 1;
            else row.in_flight += 1;
        }
        return [...m.entries()].map(([dept, v]) => ({ dept, ...v }));
    }, [appraisals]);

    const deptBarData = useMemo(() => ({
        labels: departmentStats.map((d) => d.dept),
        datasets: [
            { label: "Published", data: departmentStats.map((d) => d.published), backgroundColor: "rgba(16,185,129,0.75)", borderRadius: 3 },
            { label: "Pending HR", data: departmentStats.map((d) => d.pending_hr), backgroundColor: "rgba(139,92,246,0.75)", borderRadius: 3 },
            { label: "In Flight", data: departmentStats.map((d) => d.in_flight), backgroundColor: "rgba(59,130,246,0.55)", borderRadius: 3 },
        ],
    }), [departmentStats]);

    const deptBarOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } } },
        scales: {
            x: { stacked: true, ticks: { font: { size: 11 } }, grid: { display: false } },
            y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "rgba(148,163,184,0.2)" } },
        },
    }), []);

    // Rating distribution across published appraisals.
    const ratingDistribution = useMemo(() => {
        const m = Object.fromEntries(RATING_BANDS.map((b) => [b.label, 0]));
        for (const a of appraisals) {
            const b = bandOf(a.final_score);
            if (b && m[b] !== undefined) m[b] += 1;
        }
        return m;
    }, [appraisals]);

    const ratingRows = useMemo(
        () => RATING_BANDS
            .map((b) => ({ key: b.label, label: b.label, count: ratingDistribution[b.label], bar: b.bar }))
            .filter((row) => row.count > 0),
        [ratingDistribution],
    );
    const ratedTotal = ratingRows.reduce((sum, row) => sum + row.count, 0);

    const pendingHRList = useMemo(
        () => appraisals.filter((a) => a.status === "pending_hr_approval").slice(0, 5),
        [appraisals],
    );

    // Both derived from what is already on screen -- no extra call. The cycle
    // question HR actually asks is "how far through are we", and a bare
    // published count does not answer it without the denominator.
    const completionPercent = total ? Math.round((published * 100) / total) : null;

    const averageScore = useMemo(() => {
        const scored = appraisals
            .map((a) => a.final_score)
            .filter((s) => s !== null && s !== undefined)
            .map(Number)
            .filter((s) => !Number.isNaN(s));
        if (!scored.length) return null;
        return {
            value: (scored.reduce((sum, s) => sum + s, 0) / scored.length).toFixed(1),
            count: scored.length,
        };
    }, [appraisals]);

    return (
        <div className="space-y-4">
            <DashboardSection
                title="Organization Overview"
                action={
                    <Button size="sm" variant="brand" onClick={() => navigate("/appraisal/final-approval")}>
                        Open Final Approval <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                }
            >
                <TileGrid>
                    <StatTile Icon={Layers3} label="Total Appraisals" value={total} />
                    <StatTile
                        Icon={AlertTriangle}
                        label="Pending HR"
                        value={pendingHR}
                        tone={pendingHR ? "warning" : "default"}
                        onClick={() => navigate("/appraisal/final-approval")}
                    />
                    <StatTile
                        Icon={Award}
                        label="Published"
                        value={published}
                        tone="success"
                        hint={completionPercent === null ? undefined : `${completionPercent}% of the cycle complete`}
                    />
                    <StatTile
                        Icon={AlertTriangle}
                        label="Overdue"
                        value={overdue}
                        tone={overdue ? "danger" : "default"}
                    />
                </TileGrid>
            </DashboardSection>

            <SectionGrid>
                <DashboardSection title="Status Distribution">
                    <DistributionBars
                        rows={statusRows}
                        total={total}
                        colorFor={(row) => row.bar}
                        emptyText="No appraisals yet."
                        labelWidth="w-40"
                    />
                </DashboardSection>

                <DashboardSection
                    title="Pending HR Approval"
                    action={
                        pendingHRList.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/final-approval")}>
                                View all <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        )
                    }
                >
                    <DashboardTable
                        columns={PENDING_COLUMNS}
                        rows={pendingHRList}
                        onRowClick={(a) => navigate(`/appraisal/final-approval/${a.id}`)}
                        minWidth="min-w-[320px]"
                        empty="No appraisals waiting on your approval."
                    />
                </DashboardSection>
            </SectionGrid>

            <SectionGrid>
                <DashboardSection title="Department Progress">
                    {departmentStats.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No data yet.</p>
                    ) : (
                        <div className="h-64">
                            <Bar data={deptBarData} options={deptBarOptions} />
                        </div>
                    )}
                </DashboardSection>

                <DashboardSection
                    title="Rating Distribution"
                    action={averageScore && (
                        <p className="text-xs text-muted-foreground">
                            Average {averageScore.value} · {averageScore.count} rated
                        </p>
                    )}
                >
                    <DistributionBars
                        rows={ratingRows}
                        total={ratedTotal}
                        colorFor={(row) => row.bar}
                        emptyText="No published ratings yet."
                        labelWidth="w-40"
                    />
                </DashboardSection>
            </SectionGrid>
        </div>
    );
};

export default HRGovernanceSummary;
